import { useState, useEffect } from "react";
import TaskList from "./TaskList";
import { useApi } from "../../../../api/ApiProvider";
import Api from "../../../../axiosApi/api";
import {
  CreateTaskCommand,
  UpdateTaskCommand,
  Task,
  JiraIssue,
} from "axiosApi/models";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import { useDialog } from 'common/DialogProvider';
import JiraIssueSearch from "../../JiraIssue/JiraIssueSearch";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BugReportIcon from "@mui/icons-material/BugReport";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import intl from "components/i18n/ReactIntlWrapper";
import { handleAPIError } from "common/errorHandler";
import ErrorSummary from "components/common/ErrorSummary";

export type TaskJobInput = {
  description: string;
  idJira: string | null;
  idJob: number;
};

type TaskJobErrors = {
  description?: string;
};

type TaskJobProps = {
  idProject: number;
  idJob: number;
  JobName: string;
  ProjectName: string;
  ClientName: string;
  isJiraReady: boolean;
  closeModal: any;
};

const TaskJob = ({
  idProject,
  idJob,
  JobName,
  ProjectName,
  ClientName,
  isJiraReady,
  closeModal,
}: TaskJobProps) => {
  const DefaultTaskJobInput = {
    description: "",
    idJob: idJob,
    idJira: "",
  };

  const [input, setInput] = useState<
    CreateTaskCommand | UpdateTaskCommand | null
  >(DefaultTaskJobInput);

  const api: Api = useApi();

  const toast = useToastMessageQueue();

  const [tasksByJob, setTasksByJob] = useState<Task[] | null>();

  const [errors, setErrors] = useState<TaskJobErrors>({});

  const [showJiraImport, setShowJiraImport] = useState(false);

  const fetchTasksByJob = async (idJob: number) => {
    await api.jobsApi
      .apiVversionTaskGetByJobIdJobGet(idJob, "1", {})
      .then((response) => {
        if (response.data.data) {
          setTasksByJob(response.data.data.queryResult);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  useEffect(() => {
    if (idJob) {
      fetchTasksByJob(idJob);
    }
  }, [idJob]);

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const inputVal = {
      ...input,
      [e.target.name]: e.target.value,
    };
    setInput({ ...inputVal });
    validate({ ...inputVal });
  };

  const validate = (inputVal) => {
    let errors = {};
    if (!inputVal.description) {
      errors["description"] = intl.get("validate.errors.isRequired");
    }
    setErrors({ ...errors });
    return Object.keys(errors).length > 0 ? errors : null;
  };

  const doTaskAdd = (input: CreateTaskCommand) => {
    api.jobsApi
      .apiVversionTaskPost("1", input, {})
      .then((response) => {
        toast.success({ message: intl.get("taskJob.toast.success.doTaskAdd") });
        setInput(DefaultTaskJobInput);
        setTasksByJob((Tasks) =>
          Tasks ? [...Tasks, response.data.data] : [response.data.data]
        );
        response.data.data;
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  const handleAddTaskJob = async (e) => {
    e.preventDefault();
    const errors = validate(input);
    if (errors == null) {
      doTaskAdd(input);
    }
  };

  const handleSaveTaskJob = async (e) => {
    e.preventDefault();
    const errors = validate(input);
    if (errors == null) {
      await api.jobsApi
        .apiVversionTaskPut("1", input, {})
        .then((response) => {
          toast.success({
            message: intl.get("taskJob.toast.success.handleSaveTaskJob"),
          });
          setInput(DefaultTaskJobInput);
          setTasksByJob((Tasks) => [
            ...Tasks.map((j) => {
              if (j.id != (input as UpdateTaskCommand).id) return j;
              else return response.data.data;
            }),
          ]);
        })
        .catch((error) => {
          handleAPIError(error, toast, errors);
          setErrors({ ...errors });
        });
    }
  };

  const handleTaskEdit = (idTask: number) => {
    if (!isEditMode()) {
      const editingTask = tasksByJob.find((Task: Task) => Task.id == idTask);
      if (editingTask != null) setInput({ ...editingTask, id: idTask });
      else
        toast.error({ message: intl.get("taskJob.toast.error.handleTaskEdit") });
    } else {
      setInput(DefaultTaskJobInput);
    }
  };

  const { openDialog, closeDialog } = useDialog();

  const handleTaskDelete = (idTask: number) => {
    const id = openDialog({
      title: intl.get("delete.modal.title"),
      message: intl.get("confirm.modal.taskJob.message"),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get("delete.modal.delete.button"),
        handleConfirm: async () => {
          await api.jobsApi
            .apiVversionTaskIdDelete(idTask, "1", {})
            .then((response) => {
              if (response.status == 200) {
                toast.success({
                  message: intl.get("taskJob.toast.success.handleTaskDelete"),
                });
                setTasksByJob((Tasks) => [
                  ...Tasks.filter((j) => j.id != idTask),
                ]);
                closeDialog(id);
              } else {
                toast.error({
                  message: intl.get("taskJob.toast.error.handleTaskDelete"),
                });
              }
            })
            .catch((error) => {
              handleAPIError(error, toast, errors);
              setErrors({ ...errors });
            });
        }
      },
      cancelButton: {
        cancelLabel: intl.get("delete.modal.cancel.button"),
        handleCancel: () => {
          closeDialog(id);
        },
      }
    });
    return id;
  };

  const isEditMode = () => {
    return Object.keys(input).includes("id");
  };

  const handleCancelEdit = () => {
    setInput(DefaultTaskJobInput);
  };

  const isTaskEditMode = (idTask: number) => {
    return isEditMode() && (input as UpdateTaskCommand).id === idTask;
  };

  const handleJiraImport = () => {
    setShowJiraImport(!showJiraImport);
  };

  const handleIssuesAdd = (issues: JiraIssue[]) => {
    issues.forEach((issue) => {
      doTaskAdd({ idJob: idJob, description: issue.key, idJira: issue.idJira });
    });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="h5" display="flex" alignItems="center">
            {JobName} - {ProjectName} - {ClientName} {intl.get("taskJob.title")}
            {isJiraReady &&
              (showJiraImport ? (
                <IconButton onClick={handleJiraImport} sx={{ ml: 2 }}>
                  <AddCircleOutlineIcon />
                </IconButton>
              ) : (
                <IconButton onClick={handleJiraImport} sx={{ ml: 2 }}>
                  <BugReportIcon />
                </IconButton>
              ))}
          </Typography>

          {!showJiraImport && (
            <Box component="form" onSubmit={(e) => e.preventDefault()}>
              <Grid container spacing={2} mt={1}>
                <Grid size={12}>
                  <ErrorSummary errors={errors} showEverything={true} />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <TextField
                    autoFocus
                    id="description"
                    name="description"
                    label={intl.get("taskJob.description")}
                    placeholder={intl.get("taskJob.placeholder.description")}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    error={!!errors.description}
                    helperText={errors.description}
                    value={input.description}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 3
                  }}>
                  <TextField
                    id="idJira"
                    name="idJira"
                    label={intl.get("taskJob.jira")}
                    placeholder={intl.get("taskJob.placeholder.jira")}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={input.idJira}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid
                  display="flex"
                  alignItems="center"
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  {!isEditMode() ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddTaskJob}
                      sx={{ m: 1 }}
                    >
                      {intl.get("projectDetail.add.button")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleCancelEdit}
                        sx={{ m: 1 }}
                      >
                        {intl.get("projectDetail.cancel.button")}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveTaskJob}
                        sx={{ m: 1 }}
                      >
                        <SaveOutlinedIcon
                          className="mb-1"
                          style={{ marginRight: "4px" }}
                        />
                        {intl.get("projectDetail.save.button")}
                      </Button>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>
      </Grid>
      {showJiraImport && (
        <Box className="row m-0">
          <JiraIssueSearch
            idProject={idProject}
            handleIssuesAdd={handleIssuesAdd}
            tasks={tasksByJob}
          />
        </Box>
      )}
      <Box className="row m-0 tasklist-modal">
        <TaskList
          tasksByJob={tasksByJob}
          handleTaskEdit={handleTaskEdit}
          handleTaskDelete={handleTaskDelete}
          isTaskEditMode={isTaskEditMode}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" mt={3} mr={3}>
        <Button variant="outlined" color="inherit" onClick={closeModal}>
          {intl.get("close.button")}
        </Button>
      </Box>
    </>
  );
};

export default TaskJob;
